import { Navigate, Route } from 'react-router-dom';
import { 
  apiDocsPlugin, 
  //ApiExplorerPage 
} from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  CatalogTable,
  CatalogTableColumnsFunc,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import type { TableProps } from '@backstage/core-components';
import type { CatalogTableRow } from '@backstage/plugin-catalog';
import {
  //CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { /*ScaffolderPage,*/ scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  //TechDocsIndexPage,
  techdocsPlugin,
  //TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
//import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
//import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
//import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis.tsx'
import { platformAuthApiRef } from './apis.tsx';
import { PlatformTokenSignInPage } from './components/PlatformTokenSignInPage';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import LightIcon from '@material-ui/icons/WbSunny';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
//import { RequirePermission } from '@backstage/plugin-permission-react';
//import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';

import { UnifiedThemeProvider } from '@backstage/theme';
import { CausewayTheme } from './theme/theme';

//import { CustomTechDocsHome } from './components/CustomTechDocsHome';
import { DocusaurusDocsPage } from './components/DocusaurusDocsPage/DocusaurusDocsPage';
import { ApiKeysPage } from './components/ApiKeysPage/ApiKeysPage';
import { ApiAgentPage } from './components/ApiAgentPage';
import { HideSystemEntityChrome } from './HideSystemEntityChrome'
import { GraphiQLPage } from '@backstage-community/plugin-graphiql';

import type { TableColumn } from '@backstage/core-components';
//import { CatalogFilterLayout } from '@backstage/plugin-catalog-react';
//import { EntityListProvider } from '@backstage/plugin-catalog-react';
import { SystemsCatalogNoFiltersPage } from './components/catalog/SystemsCatalogNoFiltersPage'
import { ToolsPage } from './components/ToolsPage'


//1) Columns: for Systems only, include exactly what you want (no Owner)
// const systemColumns: CatalogTableColumnsFunc = ctx => {
//   if (ctx.filters.kind?.value === 'system') {
//     return [
//       CatalogTable.columns.createNameColumn(),
//       {
//         title: 'Description',
//         field: 'entity.metadata.description',
//         cellStyle: {
//           whiteSpace: 'normal',
//           wordBreak: 'break-word',
//           lineHeight: '1.4',
//         },
//         render: (rowData: any) => 
//           rowData?.entity?.metadata?.description ?? '—'
//       },
//     ];
//   }
//   return CatalogTable.defaultColumnsFunc(ctx);
// };


const systemColumns: CatalogTableColumnsFunc = ctx => {
  const defaults = CatalogTable.defaultColumnsFunc(ctx) as unknown as TableColumn<CatalogTableRow>[];

  if (ctx.filters.kind?.value !== 'system') {
    return defaults;
  }

  const nameCol = CatalogTable.columns.createNameColumn();

  const columns: TableColumn<CatalogTableRow>[] = [
    {
      title: nameCol.title ?? 'Name',
      field: 'entity.metadata.name',
      render: (row) => nameCol.render?.(row, 'row') as any,
// render: (row: any) => (
//   <RouterLink to={`/catalog/${row.entity.metadata.namespace ?? 'default'}/system/${row.entity.metadata.name}`}>
//     {row.entity.metadata.title ?? row.entity.metadata.name}
//   </RouterLink>
// ),
      width: '300px',
      cellStyle: {
        fontWeight: 700,
        maxWidth: '300px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    {
      title: 'Description',
      field: 'entity.metadata.description',
      cellStyle: {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        lineHeight: '1.4',
      },
      render: row => row.entity?.metadata?.description ?? '—',
    },
  ];
  return columns;
};


// 2) Actions: disable for this page (empty array)
const noActions: TableProps<CatalogTableRow>['actions'] = [];


const app = createApp({
  apis,
  plugins: [
    catalogPlugin,
    //apiDocsPlugin,
    //scaffolderPlugin,
    // techdocsPlugin,
    // catalogImportPlugin,
    //orgPlugin,
    // ...whatever else you use
  ],  
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    // Production: redirect-based OIDC PKCE flow via Platform Authentication
    // Local dev: popup-based OIDC flow for convenience
    SignInPage: props => {
      const params = new URLSearchParams(window.location.search);
      const hasAuthContext = params.has('authContext');
      const hasOidcCallback = params.has('code') && params.has('state');
      const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      // In CausewayOne iframe, returning from OIDC redirect, or direct production access
      if (hasAuthContext || hasOidcCallback || !isLocalDev) {
        return <PlatformTokenSignInPage {...props} />;
      }

      // Local dev: popup-based OIDC
      return (
        <SignInPage
          {...props}
          auto
          provider={{
            id: 'oidc',
            title: 'Platform Login',
            message: 'Sign in using Platform Login',
            apiRef: platformAuthApiRef,
          }}
        />
      );
    },
  },
  themes: [
    {
      id: 'my-theme',
      title: 'My Custom Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={CausewayTheme} children={children} />
      ),
    },
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/documentation" element={<DocusaurusDocsPage />} />
    <Route path="/" element={<Navigate to="documentation" />} />
    {/* <Route path="/catalog" element={<Navigate to="/clouds" replace />} /> */}
      {/* <Route
      path="/catalog"
      element={<RoutableExtension extension={CustomCatalogIndex} />}
    />     */}

  {/* Custom Systems Catalog*/}
  {/* <Route path="/catalog" element={<SystemsCatalogNoFiltersPage />} /> */}

{/* <Route
  path="/catalog"
  element={
    <CatalogIndexPage
      initialKind="system"
      columns={systemColumns}
      actions={noActions}
      //filters={null} // ✅ removes ALL filter UI + dropdowns
      //filters={<></>}
      tableOptions={{ paging: false, search: false, sorting: true, padding: 'dense' }}
    />
  }
/>     */}

    <Route path="/catalog" element={<SystemsCatalogNoFiltersPage />} />


    {/* <Route
      path="/clouds"
      element={<CustomCatalogSystemsPage columns={systemColumns} actions={noActions} />}
    /> */}
    <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />}>
      {entityPage}
    </Route>

    {/* <Route path="/docs" element={<TechDocsIndexPage />}>
      <CustomTechDocsHome />
    </Route> */}
    {/* Docs reader for an entity */}
    {/* <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route> */}

    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/api-keys" element={<ApiKeysPage />} />
    <Route path="/tools" element={<ToolsPage />} />
    <Route path="/api-agent" element={<ApiAgentPage />} />
    <Route path="/graphiql" element={<GraphiQLPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} />
    <OAuthRequestDialog />
    <AppRouter>
      <HideSystemEntityChrome />
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
